import { PayloadAction, createSlice, createSelector } from '@reduxjs/toolkit'
import { RootState } from 'redux/store'
import { isMobile } from 'utils'
import { getShowIsSoldOut, getShow } from 'redux/slices/liveShow'
import { showHasEnded } from 'utils/show'

export interface MessagingBannerState {
    status: BannerStatus
    message: string | null
    icon: BannerIcon
}

export interface ShowErrorBannerPayload {
    message: string
    icon?: BannerIcon
}

export enum BannerStatus {
    DEFAULT,
    WARNING,
    ERROR,
    SUCCESS,
    DISABLED,
}

export enum BannerIcon {
    NONE,
    TICKET,
    CHECK,
    ERROR,
}

export const initialState: MessagingBannerState = {
    status: BannerStatus.DEFAULT,
    message: null,
    icon: BannerIcon.NONE,
}

export enum Message {
    USER_PURCHASED_PASS = `You’re all set for the upcoming event!`,
    USER_PURCHASED_PASS_MOBILE = `You’re all set!`,
    BUY_PASS_TO_ENTER_SHOW = 'Register for the upcoming event!',
    REDEEM_ONLY_SHOW = 'Redeem your code to gain full access!',
    BUY_PASS_TO_ENTER_SHOW_MOBILE = 'Register for the upcoming event',
    CONFIRMATION_CODE_REDEEMED = 'Code redeemed!',
    SHOW_SOLD_OUT = 'The event is sold out',
    FREE_PREVIEW_STARTED = 'Your free preview has started',
    FREE_PREVIEW_ENDED = 'Your free preview has ended',
    SHOW_VOD_STATE_BANNER = 'This event has ended',
    USER_ENTERED_A_PRIVATE_EVENT = "You're all set for the upcoming event",
}

const messagingBannerSlice = createSlice({
    name: 'messagingBanner',
    initialState,
    reducers: {
        resetState: _ => initialState,
        userPurchasedPassMsg: state => {
            state.icon = BannerIcon.CHECK
            state.message = isMobile() ? Message.USER_PURCHASED_PASS_MOBILE : Message.USER_PURCHASED_PASS
            state.status = BannerStatus.SUCCESS
        },
        buyPassToEnterTheShowMsg: state => {
            state.icon = BannerIcon.TICKET
            state.message = isMobile() ? Message.BUY_PASS_TO_ENTER_SHOW_MOBILE : Message.BUY_PASS_TO_ENTER_SHOW
            state.status = BannerStatus.DEFAULT
        },
        showSoldOutMsg: state => {
            state.icon = BannerIcon.TICKET
            state.message = Message.SHOW_SOLD_OUT
            state.status = BannerStatus.DISABLED
        },
        confirmationCodeRedeemedMsg: state => {
            state.icon = BannerIcon.TICKET
            state.message = Message.CONFIRMATION_CODE_REDEEMED
            state.status = BannerStatus.SUCCESS
        },
        freePreviewStartedMsg: state => {
            state.icon = BannerIcon.TICKET
            state.message = Message.FREE_PREVIEW_STARTED
            state.status = BannerStatus.WARNING
        },
        freePreviewEndedMsg: state => {
            state.icon = BannerIcon.TICKET
            state.message = Message.FREE_PREVIEW_ENDED
            state.status = BannerStatus.ERROR
        },
        showVODStateMsg: state => {
            state.icon = BannerIcon.NONE
            state.message = Message.SHOW_VOD_STATE_BANNER
            state.status = BannerStatus.DISABLED
        },
        redeemOnlyMsg: state => {
            state.icon = BannerIcon.TICKET
            state.message = Message.REDEEM_ONLY_SHOW
            state.status = BannerStatus.DEFAULT
        },
        showErrorBanner: (state, action: PayloadAction<ShowErrorBannerPayload>) => {
            state.icon = action.payload.icon || BannerIcon.NONE
            state.message = action.payload.message
            state.status = BannerStatus.ERROR
        },
        userAccessPrivateEvent: state => {
            state.icon = BannerIcon.NONE
            state.message = Message.USER_ENTERED_A_PRIVATE_EVENT
            state.status = BannerStatus.SUCCESS
        },
        clearBanner: state => {
            state.message = null
            state.status = BannerStatus.DEFAULT
            state.icon = BannerIcon.NONE
        },
    },
})

const { actions, reducer } = messagingBannerSlice

export default reducer

export const {
    resetState,
    userPurchasedPassMsg,
    showErrorBanner,
    clearBanner,
    showSoldOutMsg,
    freePreviewStartedMsg,
    freePreviewEndedMsg,
    showVODStateMsg,
    buyPassToEnterTheShowMsg,
    confirmationCodeRedeemedMsg,
    redeemOnlyMsg,
    userAccessPrivateEvent,
} = actions

export const getMessagingBanner = (store: RootState) => store.messagingBanner

export const bannerHasNoErrors = createSelector(getMessagingBanner, banner => banner.status !== BannerStatus.ERROR)

export const showIsNotSoldOutAndBannerHasNoErrors = createSelector(
    store => getShowIsSoldOut(store),
    bannerHasNoErrors,
    (showIsSoldOut, noErrors) => !showIsSoldOut && noErrors,
)

export const showHasNotEndedAndBannerHasNoErrors = createSelector(
    store => getShow(store),
    bannerHasNoErrors,
    (show, noErrors) => {
        if (!show) return false

        return !showHasEnded(show) && noErrors
    },
)
